/** WorkTimerButton — top-bar control that shows and toggles the current user's
 * running TimeEntry. Subscribes to the same backend query as the page banner
 * so the two displays are always in sync. */

import { Button, Tooltip } from "antd";
import {
  PauseCircleOutlined,
  PlayCircleOutlined,
} from "@ant-design/icons";
import type { CSSProperties } from "react";
import { useActiveTimer } from "../../hooks/useActiveTimer";
import { formatDuration } from "../time-entries/utils";

const LABEL = "工时记录";

function formatTimerValue(elapsedMs: number): string {
  return formatDuration(Math.floor(elapsedMs / 1000));
}

const buttonStyle = (isRunning: boolean): CSSProperties => ({
  display: "inline-flex",
  alignItems: "center",
  gap: "var(--jerry-sp-1)",
  padding: "0 var(--jerry-sp-2)",
  fontVariantNumeric: "tabular-nums",
  fontWeight: "var(--jerry-fw-medium)",
  ...(isRunning
    ? {
        background: "var(--jerry-primary-bg)",
        borderColor: "var(--jerry-primary-500)",
        color: "var(--jerry-primary-700)",
      }
    : {
        color: "var(--jerry-text-secondary)",
      }),
});

const indicatorStyle = (isRunning: boolean): CSSProperties => ({
  fontSize: "var(--jerry-icon-sm)",
  display: "inline-flex",
  alignItems: "center",
  color: isRunning ? "var(--jerry-primary-500)" : "var(--jerry-text-tertiary)",
});

const labelStyle: CSSProperties = {
  color: "inherit",
};

export default function WorkTimerButton() {
  const {
    entry,
    elapsedMs,
    isRunning,
    isLoading,
    isStarting,
    isStopping,
    start,
    stop,
    error,
  } = useActiveTimer();

  const busy = isStarting || isStopping;
  const disabled = isLoading || busy;

  const handleClick = (): void => {
    if (isRunning) stop();
    else start();
  };

  const button = (
    <Button
      size="middle"
      type="default"
      data-testid="work-timer-button"
      style={buttonStyle(isRunning)}
      onClick={handleClick}
      disabled={disabled}
      loading={busy}
    >
      <span style={indicatorStyle(isRunning)}>
        {isRunning ? <PauseCircleOutlined /> : <PlayCircleOutlined />}
      </span>
      <span style={labelStyle}>{LABEL}</span>
      <span data-testid="work-timer-elapsed">{formatTimerValue(elapsedMs)}</span>
    </Button>
  );

  const tipTitle = error
    ? error
    : isRunning
      ? "停止当前计时"
      : entry
        ? "开始新计时"
        : "开始计时";

  return <Tooltip title={tipTitle}>{button}</Tooltip>;
}